import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router-dom";

import BottomNav from "./components/BottomNav";
import BrandLockup from "./components/brand/BrandLockup";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FeaturesProvider, useFeatures } from "./context/FeaturesContext";
import LoginScreen from "./routes/LoginScreen";
import LinkSignInPage from "./routes/LinkSignInPage";
import ClockScreen from "./routes/ClockScreen";
import HomePage from "./routes/HomePage";
import ProfileScreen from "./routes/ProfileScreen";
import LeaveActionPage from "./routes/LeaveActionPage";
import LeavePage from "./routes/LeavePage";
import DocumentsPage from "./routes/DocumentsPage";
import TrainingPage from "./routes/TrainingPage";
import ExpensesPage from "./routes/ExpensesPage";
import PerformancePage from "./routes/PerformancePage";
import OrgChartPage from "./routes/OrgChartPage";
import DirectoryPage from "./routes/DirectoryPage";
import ReviewPage from "./routes/ReviewPage";
import SocialFeedPage from "./routes/SocialFeedPage";
import TeamPage from "./routes/TeamPage";
import JobDescriptionPage from "./routes/JobDescriptionPage";
import GlobalViewAs from "./components/GlobalViewAs";
import PinChangeOverlay from "./components/PinChangeOverlay";
import VersionWatcher from "./components/VersionWatcher";
import CompanyCalendarPage from "./routes/CompanyCalendarPage";
import HelpPage from "./routes/HelpPage";
import FeatureGate from "./components/FeatureGate";
import AdminLayout from "./routes/admin/AdminLayout";
import AbsenceCalendar from "./routes/admin/AbsenceCalendar";
import AdminAnnouncementsPage from "./routes/admin/AdminAnnouncementsPage";
import AdminCompliancePage from "./routes/admin/AdminCompliancePage";
import AdminDashboard from "./routes/admin/AdminDashboard";
import AdminDocumentsPage from "./routes/admin/AdminDocumentsPage";
import AdminExpensesPage from "./routes/admin/AdminExpensesPage";
import AdminLeavePage from "./routes/admin/AdminLeavePage";
import AdminOnboardingPage from "./routes/admin/AdminOnboardingPage";
import AdminPayrollPage from "./routes/admin/AdminPayrollPage";
import AdminPerformancePage from "./routes/admin/AdminPerformancePage";
import AdminTeamsPage from "./routes/admin/AdminTeamsPage";
import AdminTrainingPage from "./routes/admin/AdminTrainingPage";
import BulkUploadPage from "./routes/admin/BulkUploadPage";
import EmployeeList from "./routes/admin/EmployeeList";
import EmployeeForm from "./routes/admin/EmployeeForm";
import ReportsPage from "./routes/admin/ReportsPage";
import SettingsPage from "./routes/admin/SettingsPage";
import SiteSettings from "./routes/admin/SiteSettings";

function ComingSoon({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-dvh flex-col safe-x">
      <header className="safe-top px-5 pt-4 pb-2">
        <h1 className="text-2xl font-semibold">{title}</h1>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 text-center">
        <p className="text-lg text-gray-500">{t("common.comingSoon")}</p>
      </main>
      <BottomNav />
    </div>
  );
}

function Timesheets() {
  const { t } = useTranslation();
  return <ComingSoon title={t("nav.timesheets")} />;
}

function LoadingScreen() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-6">
        <BrandLockup stacked size="lg" />
        <div
          role="status"
          aria-label="Loading"
          className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-pme-blue"
        />
      </div>
    </div>
  );
}

/** Shared site tablets (kiosk sessions) are for clocking only, so they skip
 *  the personal home page and go straight to the clock. */
function HomeOrClock() {
  const { isKiosk } = useAuth();
  return isKiosk ? <Navigate to="/clock" replace /> : <HomePage />;
}

function AppRoutes() {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const { isLoaded: featuresLoaded } = useFeatures();

  if (authLoading || (isAuthenticated && !featuresLoaded)) {
    return <LoadingScreen />;
  }

  return (
    <>
      <VersionWatcher />
      <PinChangeOverlay />
      <GlobalViewAs />
      <Routes>
        <Route path="/" element={<LoginScreen />} />
        <Route path="/link/:token" element={<LinkSignInPage />} />

        <Route path="/home" element={<ProtectedRoute><HomeOrClock /></ProtectedRoute>} />
        <Route path="/clock" element={<ProtectedRoute><ClockScreen /></ProtectedRoute>} />
        <Route path="/me" element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} />
        <Route path="/timesheets" element={<ProtectedRoute><Timesheets /></ProtectedRoute>} />
        <Route path="/leave" element={<ProtectedRoute><LeavePage /></ProtectedRoute>} />
        <Route path="/leave/action" element={<ProtectedRoute><LeaveActionPage /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
        <Route path="/training" element={<ProtectedRoute><FeatureGate flag="training_enabled"><TrainingPage /></FeatureGate></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
        <Route path="/performance" element={<ProtectedRoute><PerformancePage /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
        <Route path="/job-description" element={<ProtectedRoute><JobDescriptionPage /></ProtectedRoute>} />
        <Route path="/job-description/:employeeId" element={<ProtectedRoute><JobDescriptionPage /></ProtectedRoute>} />
        <Route path="/review" element={<ProtectedRoute><ReviewPage /></ProtectedRoute>} />
        <Route path="/org-chart" element={<ProtectedRoute><FeatureGate flag="org_chart_enabled"><OrgChartPage /></FeatureGate></ProtectedRoute>} />
        <Route path="/directory" element={<ProtectedRoute><DirectoryPage /></ProtectedRoute>} />
        <Route path="/recognition" element={<Navigate to="/social" replace />} />
        <Route path="/social" element={<ProtectedRoute><SocialFeedPage /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><CompanyCalendarPage /></ProtectedRoute>} />
        <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />

        <Route
          path="/admin"
          element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}
        >
          <Route index element={<AdminDashboard />} />
          <Route path="employees" element={<EmployeeList />} />
          <Route path="employees/new" element={<EmployeeForm />} />
          <Route path="employees/bulk-upload" element={<BulkUploadPage />} />
          <Route path="employees/:id" element={<EmployeeForm />} />
          <Route path="teams" element={<AdminTeamsPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="payroll" element={<AdminPayrollPage />} />
          <Route path="sites" element={<SiteSettings />} />
          <Route path="leave" element={<AdminLeavePage />} />
          <Route path="calendar" element={<AbsenceCalendar />} />
          <Route path="training" element={<AdminTrainingPage />} />
          <Route path="compliance" element={<AdminCompliancePage />} />
          <Route path="documents" element={<AdminDocumentsPage />} />
          <Route path="performance" element={<AdminPerformancePage />} />
          <Route path="onboarding" element={<AdminOnboardingPage />} />
          <Route path="expenses" element={<AdminExpensesPage />} />
          <Route path="announcements" element={<AdminAnnouncementsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<LoginScreen />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <FeaturesProvider>
        <AppRoutes />
      </FeaturesProvider>
    </AuthProvider>
  );
}
